import Range from '@app/model/_common/Range';
import Coordinates from '@app/model/hotel/Coordinates';

interface SearchHotelsFilterInputInterface {
	companyHotelId: number | undefined;
	coordinates: Coordinates | undefined;
	radiusInMeters: number | undefined;
	isSuperhost: boolean | undefined;
	bedrooms: number[];
	moreThanBedrooms: number | undefined;
	bathrooms: number[];
	moreThanBathrooms: number | undefined;
	guests: number[];
	moreThanGuests: number | undefined;
	medianPrice: Range | undefined;
	rating: Range | undefined;
	reviewsCount: Range | undefined;
}

class SearchHotelsFilterInput implements SearchHotelsFilterInputInterface {
	companyHotelId: number | undefined;
	coordinates: Coordinates | undefined;
	radiusInMeters: number | undefined;
	isSuperhost: boolean | undefined = undefined;
	bedrooms: number[] = [];
	moreThanBedrooms: number | undefined = undefined;
	bathrooms: number[] = [];
	moreThanBathrooms: number | undefined = undefined;
	guests: number[] = [];
	moreThanGuests: number | undefined = undefined;
	medianPrice: Range | undefined = undefined;
	rating: Range | undefined = undefined;
	reviewsCount: Range | undefined = undefined;

	constructor(input: SearchHotelsFilterInputInterface) {
		Object.assign(this, input);
	}

	public addBedroom(bedroom: number) {
		if (!this.bedrooms.includes(bedroom)) this.bedrooms.push(bedroom);
	}

	public removeBedroom(bedroom: number) {
		this.bedrooms = this.bedrooms.filter(b => b !== bedroom);
	}

	public hasBedroom(bedroom: number) {
		return this.bedrooms.includes(bedroom);
	}

	public addBathroom(bathroom: number) {
		if (!this.bathrooms.includes(bathroom)) this.bathrooms.push(bathroom);
	}

	public removeBathroom(bathroom: number) {
		this.bathrooms = this.bathrooms.filter(b => b !== bathroom);
	}

	public hasBathroom(bathroom: number) {
		return this.bathrooms.includes(bathroom);
	}

	public addGuest(guest: number) {
		if (!this.guests.includes(guest)) this.guests.push(guest);
	}

	public removeGuest(guest: number) {
		this.guests = this.guests.filter(g => g !== guest);
	}

	public hasGuest(guest: number) {
		return this.guests.includes(guest);
	}

	public setIsSuperhost(isSuperhost: boolean | undefined) {
		this.isSuperhost = isSuperhost;
	}

	public removeIsSuperhost() {
		this.isSuperhost = undefined;
	}

	public setMoreThanBedrooms(moreThanBedrooms: number | undefined) {
		this.moreThanBedrooms = moreThanBedrooms;
	}

	public removeMoreThanBedrooms() {
		this.moreThanBedrooms = undefined;
	}

	public setMoreThanBathrooms(moreThanBathrooms: number | undefined) {
		this.moreThanBathrooms = moreThanBathrooms;
	}

	public removeMoreThanBathrooms() {
		this.moreThanBathrooms = undefined;
	}

	public setMoreThanGuests(moreThanGuests: number | undefined) {
		this.moreThanGuests = moreThanGuests;
	}

	public removeMoreThanGuests() {
		this.moreThanGuests = undefined;
	}

	public setMedianPrice(medianPriceFrom: number, medianPriceTo: number) {
		this.medianPrice = { from: medianPriceFrom, to: medianPriceTo };
	}

	public removeMedianPrice() {
		this.medianPrice = undefined;
	}

	public setRating(ratingFrom: number, ratingTo: number) {
		this.rating = { from: ratingFrom, to: ratingTo };
	}

	public removeRating() {
		this.rating = undefined;
	}

	public setReviewsCount(reviewsCountFrom: number, reviewsCountTo: number) {
		this.reviewsCount = { from: reviewsCountFrom, to: reviewsCountTo };
	}

	public removeReviewsCount() {
		this.reviewsCount = undefined;
	}

	public setRadiusInMeters(radiusInMeters: number) {
		this.radiusInMeters = radiusInMeters;
	}

	public clear() {
		this.bedrooms = [];
		this.bathrooms = [];
		this.guests = [];
		this.isSuperhost = undefined;
		this.moreThanBedrooms = undefined;
		this.moreThanBathrooms = undefined;
		this.moreThanGuests = undefined;
		this.medianPrice = undefined;
		this.rating = undefined;
		this.reviewsCount = undefined;
		this.radiusInMeters = 20000;
	}

	public isEmpty() {
		return this.bedrooms.length === 0
			&& this.bathrooms.length === 0
			&& this.guests.length === 0
			&& this.isSuperhost === undefined
			&& this.moreThanBedrooms === undefined
			&& this.moreThanBathrooms === undefined
			&& this.moreThanGuests === undefined
			&& this.medianPrice === undefined
			&& this.rating === undefined
			&& this.reviewsCount === undefined;
	}
}

export const emptySearchHotelsFilterInput = (companyHotelId: number, coordinates: Coordinates, radiusInMeters: number) => new SearchHotelsFilterInput({
	companyHotelId: companyHotelId,
	coordinates: coordinates,
	radiusInMeters: radiusInMeters,
	isSuperhost: undefined,
	bedrooms: [],
	moreThanBedrooms: undefined,
	bathrooms: [],
	moreThanBathrooms: undefined,
	guests: [],
	moreThanGuests: undefined,
	medianPrice: undefined,
	rating: undefined,
	reviewsCount: undefined,
});


export default SearchHotelsFilterInput;